// packages/web/src/hooks/useServiceData.ts
// Hook for service data fetching with caching and error handling

import { useState, useEffect } from 'react';

import { MarketplaceService } from '../types/pricing';

interface UseServiceDataResult {
  service: MarketplaceService | null;
  loading: boolean;
  error: string | null;
  refetch: (term?: number) => void;
}

export const useServiceData = (
  serviceId: string | undefined,
  initialService?: MarketplaceService,
  termMonths?: number,
  catalogSlug?: string,
): UseServiceDataResult => {
  const [service, setService] = useState<MarketplaceService | null>(initialService || null);
  const [loading, setLoading] = useState(!initialService);
  const [error, setError] = useState<string | null>(null);

  const fetchService = async (id: string, term?: number) => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const baseUrl = catalogSlug
        ? `/api/catalog-context/${encodeURIComponent(catalogSlug)}/services/${id}`
        : `/api/catalog/services/${id}`;
      const url = term ? `${baseUrl}?term=${term}` : baseUrl;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch service: ${response.status}`);
      }
      const data = await response.json();
      setService(data.service || data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load service');
    } finally {
      setLoading(false);
    }
  };

  const refetch = (term?: number) => {
    if (serviceId) {
      fetchService(serviceId, term);
    }
  };

  useEffect(() => {
    if (serviceId && !initialService) {
      fetchService(serviceId, termMonths);
    }
  }, [serviceId, initialService, termMonths, catalogSlug]);

  return {
    service,
    loading,
    error,
    refetch,
  };
};
